import { isValidPhoneNumber } from 'libphonenumber-js';
import { MessageDescriptor } from 'react-intl';
import { PartialFieldMetaDataOverride, PartialFieldRule } from 'utils/hooks/data/useFieldMetaData';
import { CommonFieldDefinitionValueModel, FieldRuleCondition } from '@utmost.co/enterprise-models';
import { objectUtil, FieldValidatorInterface } from '@utmost.co/smart-form';
import { messages } from './ElementValidatorMessages';

function meetsCondition(
  condition: FieldRuleCondition,
  formikValues: any,
  contextValueMap: Map<string, object> | undefined
): boolean {
  let contextValue: any = '';

  if (contextValueMap && contextValueMap.has(condition.field)) {
    contextValue = contextValueMap.get(condition.field);
  }

  const fieldValue = contextValue || objectUtil.getIn(formikValues, condition.field);
  if (fieldValue) {
    if (condition.operator === 'IN') {
      return condition.values.includes(fieldValue);
    }
    if (condition.operator === 'IS_NOT_NULL') {
      return !!fieldValue;
    }
  }

  return false;
}

function startsWitValidNumber(value: string) {
  const parsedFloat = parseFloat(value);
  return !Number.isNaN(parsedFloat);
}

function createCustomMessageError(errorObject: MessageDescriptor, errorMessage?: string): MessageDescriptor {
  if (!errorMessage) {
    return errorObject;
  }
  return { ...errorObject, defaultMessage: errorMessage };
}

function getRegularExpressionBasedErrors(
  value: string,
  fieldDefinition: PartialFieldMetaDataOverride,
  rule: PartialFieldRule | undefined
) {
  const relevantRegex = rule?.regex ? rule.regex : fieldDefinition.regex;
  const relevantMessage = rule?.regex ? rule.message : fieldDefinition.message;

  if (relevantRegex) {
    try {
      const ruleRegExp = RegExp(relevantRegex);
      if (!ruleRegExp.test(value)) {
        return [createCustomMessageError(messages.STRING_DOES_NOT_MATCH_REGEXP_ERROR, relevantMessage)];
      }
    } catch (e) {
      return [messages.BAD_REGULAR_EXPRESSION_IN_DEFINITION_ERROR];
    }
  }
  return [];
}

function getAllowedValuesErrors(
  value: string,
  relevantDefinition: PartialFieldMetaDataOverride,
  rule: PartialFieldRule | undefined
) {
  const relevantValuesDefinition = rule?.values ? rule.values : relevantDefinition.values;

  if (relevantValuesDefinition?.length) {
    const valueList = relevantValuesDefinition.map(
      (allowedValue: CommonFieldDefinitionValueModel) => allowedValue.value
    );

    if (!valueList.includes(value)) {
      return [createCustomMessageError(messages.UNALLOWED_VALUE_ERROR, relevantDefinition.message)];
    }
  }
  return [];
}

const BASIC_ISO_8601 = /^\d{4}((-(0[1-9]|1[0-2]))(-((0[1-9])|([1-2][0-9])|(3[0-1])))?)?$/;
const BASIC_ISO_8601_TIME = '^([0-1][0-9]|2[0-3])(:[0-5][0-9])?(:[0-5][0-9])?$';
const EMPTY_MAP = new Map();

function getConditionalRequiredness(
  fieldDefinition: PartialFieldMetaDataOverride,
  currentValues: {},
  contextMap: Map<string, object> | undefined
): boolean | undefined {
  const firstApplicableRule = fieldDefinition?.rules?.find(
    (validation) => !validation.condition || meetsCondition(validation.condition, currentValues, contextMap)
  );

  return firstApplicableRule?.required;
}

function isRequired(
  fieldDefinition: PartialFieldMetaDataOverride | undefined,
  currentValues: object,
  contextMap: Map<string, object> | undefined
): boolean {
  if (fieldDefinition === undefined) {
    return false;
  }
  const requiredConditionally = getConditionalRequiredness(fieldDefinition, currentValues, contextMap);
  if (requiredConditionally) {
    return requiredConditionally;
  }
  return fieldDefinition.required;
}

export class FieldMetadataElementValidator implements FieldValidatorInterface {
  private fieldDefinitionsMap: Map<string, PartialFieldMetaDataOverride>;

  constructor(fieldDefinitions: PartialFieldMetaDataOverride[]) {
    const fieldDefinitionsPairs = fieldDefinitions.map(
      (fieldDefinition: PartialFieldMetaDataOverride) =>
        [fieldDefinition.urn, fieldDefinition] as [string, PartialFieldMetaDataOverride]
    );
    this.fieldDefinitionsMap = new Map<string, PartialFieldMetaDataOverride>(fieldDefinitionsPairs);
  }

  isFieldRequired(urn: string, currentValues: object, contextMap: Map<string, object> | undefined): boolean {
    const relevantDefinition = this.fieldDefinitionsMap.get(urn);

    return isRequired(relevantDefinition, currentValues, contextMap);
  }

  isFieldDisallowed(urn: string, values: object, contextMap: Map<string, object> | undefined): boolean {
    const fieldDefinition = this.getFieldMetadataFor(urn);
    const firstApplicableRule = this.getFirstApplicableRule(urn, values, contextMap || new Map());

    if (firstApplicableRule?.enabled === undefined || firstApplicableRule?.enabled === null) {
      return fieldDefinition?.enabled === false;
    }
    return !firstApplicableRule.enabled;
  }

  getFieldMetadataFor(urn: string): PartialFieldMetaDataOverride | undefined {
    return this.fieldDefinitionsMap.get(urn);
  }

  getFieldDefinitionModel(urn: string): PartialFieldMetaDataOverride | undefined {
    return this.fieldDefinitionsMap.get(urn);
  }

  getRequiredFieldsFor(entityURN: string, currentValues: {}, contextMap: Map<string, object> = EMPTY_MAP): string[] {
    const fieldDefinitionsOfTargetEntity = Array.from(this.fieldDefinitionsMap.entries()).filter(([urn]) =>
      urn.toString().startsWith(entityURN)
    );

    return fieldDefinitionsOfTargetEntity
      .filter(([, fieldDefinition]) => isRequired(fieldDefinition, currentValues, contextMap))
      .map(([urn]) => urn);
  }

  getValidationErrors(urn: string, value: string | undefined, contextMap: Map<string, object>): MessageDescriptor[] {
    const relevantDefinition = this.fieldDefinitionsMap.get(urn);

    if (!relevantDefinition) {
      return [messages.URN_DEFINITION_NOT_FOUND_ERROR];
    }
    const isEmptyValue = value === undefined;
    if (isEmptyValue) {
      if (isRequired(relevantDefinition, {}, contextMap)) {
        return [messages.REQUIRED_FIELD_MISSING];
      }
      return [];
    }

    // helping the type system realise value is not null or undefined

    if (value !== undefined) {
      const errors = [];
      const firstApplicableRule = this.getFirstApplicableRule(urn, {}, contextMap);

      const regexErrors = getRegularExpressionBasedErrors(value, relevantDefinition, firstApplicableRule);
      if (regexErrors.length > 0) {
        errors.push(regexErrors);
      }

      switch (relevantDefinition.type) {
        case 'DATE': {
          const doesNotMatchDateRegex = !BASIC_ISO_8601.test(value);
          const cannotBeParsedToValidDate = Number.isNaN(new Date(value).getTime());
          if (doesNotMatchDateRegex || cannotBeParsedToValidDate) {
            errors.push([messages.DATE_VALIDATION_ERROR]);
          }
          break;
        }
        case 'TIME': {
          const timeRegExp = RegExp(relevantDefinition.regex || BASIC_ISO_8601_TIME);
          const doesNotMatchTimeRegex = !timeRegExp.test(value);
          if (doesNotMatchTimeRegex) {
            errors.push([messages.TIME_VALIDATION_ERROR]);
          }
          break;
        }
        case 'OBJECT': {
          // We have no real way to validate objects that is useful.
          break;
        }
        case 'TEXT':
        case 'STRING': {
          errors.push(getAllowedValuesErrors(value, relevantDefinition, firstApplicableRule));
          break;
        }
        case 'MULTI_VALUE_TYPED': {
          errors.push(getAllowedValuesErrors(value, relevantDefinition, firstApplicableRule));
          break;
        }
        // Fallthrough to allow for validation of multiple types with one validator
        case 'DECIMAL':
        case 'INTEGER':
        case 'NUMBER': {
          if (!startsWitValidNumber(value)) {
            errors.push([messages.INVALID_NUMBER_ERROR]);
          }
          break;
        }
        case 'PHONE_NUMBER': {
          if (!isValidPhoneNumber(value)) {
            errors.push([messages.INVALID_PHONE_NUMBER_ERROR]);
          }
          break;
        }
      }
      return errors.flat();
    }

    return [];
  }

  private getFirstApplicableRule(
    urn: string,
    formikValues: any,
    contextMap: Map<string, object> = EMPTY_MAP
  ): PartialFieldRule | undefined {
    return this.getRules(urn, formikValues, contextMap)[0];
  }

  private getRules(urn: string, formikValues: any, contextMap: Map<string, object> = EMPTY_MAP): PartialFieldRule[] {
    const applicableDefinition = this.fieldDefinitionsMap.get(urn);

    const rules = applicableDefinition?.rules?.filter(
      ({ condition }) => !condition || meetsCondition(condition, formikValues, contextMap)
    );

    return rules || [];
  }

  getAllowedValuesFor(urn: string, values: object, contextMap: Map<string, object> | undefined): string[] | undefined {
    return this.getAllowedOptionsFor(urn, values, contextMap)?.map((value) => value.value);
  }

  getAllowedOptionsFor(
    urn: string,
    values: object,
    contextMap: Map<string, object> | undefined
  ): CommonFieldDefinitionValueModel[] | undefined {
    const rule = this.getFirstApplicableRule(urn, values, contextMap);
    if (!rule) {
      const definition = this.getFieldDefinitionModel(urn);
      return definition?.values;
    }
    return rule.values;
  }
}
